import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserInfoDto } from '@app/core/app-settings/models/app-settings.model';
import { AUTH_CONTEXT_CLAIMS, isPlatformMode, PlatformMode } from '@app/core/models/platform-mode.model';
import { LoggingService } from '@app/core/services';
import { TenantStateService } from '@app/core/services/tenant-state.service';
import { AppSettingsStore } from '@app/core/state/app-settings-store/app-settings-store';
import { AuthStore } from '@app/core/state/auth.store';
import { EntityStore } from '@app/core/state/entity.store';
import { AuthContext } from '@modules/auth/services/auth-context';
import { AuthUser as User } from '@modules/auth/models/user-oidc';
import { UserService } from './user.service';
import {
    EventTypes,
    LoginResponse,
    OidcSecurityService,
    PublicEventsService,
} from 'angular-auth-oidc-client';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type LogoutReason = 'manual' | 'idle' | 'session-expired';

export interface AuthenticatedTenantContext {
    accessToken: string;
    tenantId: string;
    userId: string;
    databaseName: string | null;
    roles: string[];
}

export type AuthenticatedTenantContextResult =
    | { status: 'ready'; context: AuthenticatedTenantContext }
    | { status: 'authentication-unavailable' }
    | { status: 'tenant-unavailable' };

type CrossTabSessionEvent = {
    type: 'activity' | 'logout';
    at: number;
    reason?: LogoutReason;
};

type SecurityContextLoadOptions = {
    force: boolean;
};

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private readonly EXPECTED_UNAUTHENTICATED_ERRORS = new Set([
        'login_required',
        'consent_required',
        'interaction_required',
        'account_selection_required',
    ]);

    // Collapses concurrent "session expired" signals (e.g. many parallel 401s)
    // into a single logout so requests cannot trigger redirect storms.
    private readonly SESSION_EXPIRED_DEBOUNCE_MS = 500;
    private readonly AUTOMATIC_RENEWAL_TIMEOUT_MS = 30 * 1000;
    // Explicit inactivity policy: log the user out after this much time with no
    // user activity. This is deliberately decoupled from the access-token
    // lifetime so a short token cannot cause premature idle logouts and so the
    // inactivity window is an intentional, single source of truth.
    private readonly IDLE_INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;
    // Keep a safety window between a protected request and access-token expiry.
    // This prevents long-running task requests from starting with a token that
    // will expire while the request is being sent or retried.
    private readonly TOKEN_RENEWAL_SKEW_SECONDS = 60;
    private readonly IDLE_ACTIVITY_EVENTS: ReadonlyArray<keyof WindowEventMap> = [
        'click',
        'mousemove',
        'keydown',
        'scroll',
        'touchstart'
    ];
    private readonly POST_LOGOUT_ROUTE = '/auth/login';
    private readonly LOGOUT_REASON_STORAGE_KEY = 'auth_logout_reason';
    private readonly CROSS_TAB_SESSION_EVENT_KEY = 'auth_session_event';
    private readonly ACTIVITY_BROADCAST_THROTTLE_MS = 5 * 1000;

    // private
    private _user: User | undefined;
    private tokenRenewInFlight: Promise<User> | null = null;
    private automaticRenewalInFlight: Promise<User | null> | null = null;
    private automaticRenewalResolver: ((user: User | null) => void) | null = null;
    private automaticRenewalTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
    private idleEventsRegistered = false;
    private idleTimeoutHandle: number | null = null;
    private lastActivityAt = Date.now();
    private readonly idleActivityListener = () => this.handleUserActivity();
    private readonly crossTabSessionListener = (event: StorageEvent) => this.handleCrossTabSessionEvent(event);
    private lastActivityBroadcastAt = 0;
    private pendingLogoutReason: LogoutReason | null = null;
    private securityContextLoadInFlight: Promise<boolean> | null = null;
    private securityContextLoadInFlightIsForced = false;
    private securityContextLoadSequence = 0;
    private readonly _loginChangedSubject = new Subject<boolean>();
    private readonly _sessionExpiredSubject = new Subject<void>();
    private sessionExpiredInProgress = false;
    private logoutInProgress = false;
    private oidcInitializationCompleted = false;

    // Public
    public readonly loginChanged: Observable<boolean> = this._loginChangedSubject.asObservable();
    public readonly sessionExpired: Observable<void> = this._sessionExpiredSubject.asObservable();
    public authContext: AuthContext | undefined;

    // Constructor
    constructor(
        private readonly _httpClient: HttpClient,
        private readonly userService: UserService,
        private readonly appSettingsStore: AppSettingsStore,
        private readonly loggingService: LoggingService,
        private readonly authStore: AuthStore,
        private readonly entityStore: EntityStore,
        private readonly tenantStateService: TenantStateService,
        private readonly router: Router,
        private readonly oidcSecurityService: OidcSecurityService,
        private readonly publicEventsService: PublicEventsService
    ) {
        this.registerOidcEvents();
        this.registerSessionExpiredHandler();
        this.registerCrossTabSessionEvents();
    }

    // Public Authentication Methods

    public isDomainAllowed(): boolean {
        const domain = this.authContext?.domain;
        if (!domain) {
            return true;
        }
        try {
            const claimOrigin = new URL(domain).origin;
            const appOrigin = new URL(environment.clientRoot).origin;
            return appOrigin === claimOrigin;
        } catch {
            return false;
        }
    }

    /** Redirect to IdentityServer login page */
    public login(): Promise<void> {
        this.loggingService.info('[AuthService] Initiating login redirect');
        this.oidcSecurityService.authorize();
        return Promise.resolve();
    }

    /** Check if user has valid authentication */
    public async isLoggedIn(): Promise<boolean> {
        if (this.isSessionTerminationInProgress()) {
            return false;
        }

        if (this._user && this.isTokenReadyForRequest(this._user)) {
            this.markActivity();
            return true;
        }

        try {
            const current = await this.loadCurrentUser();
            if (current && !this.isTokenExpired(current)) {
                this.syncUser(current);
                if (this.isTokenReadyForRequest(current)) {
                    this.markActivity();
                    return true;
                }
            }

            this._user = await this.renewToken();
            return this._user != null && !this._user.expired;
        } catch (err: any) {
            if (this.isExpectedUnauthenticatedError(err)) {
                if (!this.hasLiveAccessToken()) {
                    this.clearCachedAuthState();
                }
                return this.hasLiveAccessToken();
            }
            this.loggingService.error('[AuthService] Error checking login status', err);
            // A transient refresh/network failure must not sign out a user whose
            // current access token is still within its server-defined lifetime.
            return this.hasLiveAccessToken();
        }
    }

    /** Get valid user, renewing token if needed */
    public async getValidUser(): Promise<User | null> {
        if (this.isSessionTerminationInProgress()) {
            return null;
        }

        if (this._user && this.isTokenReadyForRequest(this._user)) {
            this.markActivity();
            return this._user;
        }

        try {
            const current = await this.loadCurrentUser();
            if (current && !this.isTokenExpired(current)) {
                this.syncUser(current);
                if (this.isTokenReadyForRequest(current)) {
                    this.markActivity();
                    return current;
                }
            }

            const renewedUser = await this.renewToken();
            if (!this.isTokenReadyForRequest(renewedUser)) {
                this.loggingService.warn(
                    '[AuthService] Token renewal returned a token too close to expiry; protected work is blocked.'
                );
                return null;
            }

            this.markActivity();
            return renewedUser;
        } catch (error) {
            if (this.isExpectedUnauthenticatedError(error)) {
                if (!this.hasLiveAccessToken()) {
                    this.clearCachedAuthState();
                }
                return null;
            }

            this.loggingService.error('[AuthService] Unable to obtain valid user', error);
            // Keep a still-live access token and its application state when a
            // refresh attempt fails transiently. The next protected action can
            // retry renewal; it must not start with an empty/stale bearer.
            if (!this.hasLiveAccessToken()) {
                this.notifySessionExpired();
            }
            return null;
        }
    }

    /** Get valid access token, renewing if needed */
    public async getValidAccessToken(): Promise<string> {
        const user = await this.getValidUser();
        return user?.access_token ?? '';
    }

    /** Resolve one coherent bearer/subject/tenant snapshot for tenant-scoped work. */
    public async getAuthenticatedTenantContext(): Promise<AuthenticatedTenantContextResult> {
        const user = await this.getValidUser();
        const accessToken = user?.access_token?.trim() || '';
        if (!user || user.expired || !accessToken) {
            return { status: 'authentication-unavailable' };
        }

        const profile = (user.profile ?? {}) as Record<string, unknown>;
        const userId = this.getClaimValue(profile, 'sub') || this.authStore.getUserId()?.trim() || '';
        const tenantId =
            this.getClaimValue(profile, 'tenant_id') ||
            this.tenantStateService.getCurrentTenantId()?.trim() ||
            '';

        if (!userId || !tenantId || tenantId.toLowerCase() === 'system') {
            return { status: 'tenant-unavailable' };
        }

        const rawRoles = profile['role'] ?? profile['roles'];
        const roles = (Array.isArray(rawRoles) ? rawRoles : [rawRoles])
            .filter((role): role is string => typeof role === 'string' && role.trim().length > 0)
            .map((role) => role.trim());

        return {
            status: 'ready',
            context: {
                accessToken,
                tenantId,
                userId,
                databaseName: this.getClaimValue(profile, 'database_name'),
                roles,
            },
        };
    }

    /**
     * Lightweight session gate for resolvers and long-running operations.
     * Returns true only when a non-empty access token is currently obtainable
     * (renewing if needed), so protected work never starts without a token.
     */
    public async isSessionValid(): Promise<boolean> {
        const token = await this.getValidAccessToken();
        return token.length > 0;
    }

    /** True once protected requests must stop while logout/expiry cleanup runs. */
    public isSessionTerminationInProgress(): boolean {
        return this.sessionExpiredInProgress || this.logoutInProgress;
    }

    /**
     * Surface a single, debounced "session expired" signal.
     * The interceptor calls this instead of logging out directly so that many
     * concurrent failing requests collapse into one central logout.
     */
    public notifySessionExpired(force = false): void {
        if (this.isSessionTerminationInProgress()) {
            return;
        }

        // Automatic renewal starts before access-token expiry. A failed or
        // timed-out renewal during this window is not permission to log out:
        // the current bearer remains valid and can be used while renewal is
        // retried. Logout is deferred until there is no live access token.
        if (!force && this.hasLiveAccessToken()) {
            this.loggingService.warn(
                '[AuthService] Session-expired signal deferred while the current access token is still valid.'
            );
            return;
        }

        // Close the protected-request gate immediately. The debounce below only
        // coalesces navigation/logout work; it must not allow more API traffic.
        this.sessionExpiredInProgress = true;
        this._sessionExpiredSubject.next();
    }

    /**
     * Loads AuthContext once per authenticated session and stores platform_mode.
     * Route guards await this so they never classify an unknown mode as tenant access.
     */
    public async ensureSecurityContextLoaded(forceReload = false): Promise<boolean> {
        if (!forceReload && this.authStore.getPlatformMode()) {
            return true;
        }

        if (!this.securityContextLoadInFlight || (forceReload && !this.securityContextLoadInFlightIsForced)) {
            this.securityContextLoadInFlightIsForced = forceReload;
            const sequenceId = ++this.securityContextLoadSequence;
            this.securityContextLoadInFlight = this.loadSecurityContext({ force: forceReload }, sequenceId)
                .finally(() => {
                    if (sequenceId === this.securityContextLoadSequence) {
                        this.securityContextLoadInFlight = null;
                        this.securityContextLoadInFlightIsForced = false;
                    }
                });
        }

        return this.securityContextLoadInFlight;
    }

    public isExpectedUnauthenticatedError(error: unknown): boolean {
        const message = this.extractAuthErrorMessage(error);
        return this.EXPECTED_UNAUTHENTICATED_ERRORS.has(message);
    }

    /**
     * Complete login after redirect from IdentityServer.
     *
     * The redirect callback (authorization code exchange) is processed by
     * `checkAuth()` during `initializeUser()` (APP_INITIALIZER), which runs
     * before this callback route activates. Here we read the resulting session,
     * populate the app stores and load the security context.
     */
    public async completeLogin(): Promise<User> {
        const user = await this.loadCurrentUser();
        if (!user) {
            this.loggingService.error('[AuthService] Login callback completed without an authenticated user');
            await this.clearFailedLoginState();
            throw new Error('Authentication did not produce a valid user.');
        }

        this._user = user;

        this.loggingService.info('[AuthService] New session started');

        const mappedUser = this.extractUserClaims(user);
        this.updateUserStores(mappedUser, user);
        this.entityStore.initializeFromAuth(mappedUser);
        this.userService.user = user;

        try {
            const securityContextLoaded = await this.ensureSecurityContextLoaded(true);
            if (!securityContextLoaded) {
                throw new Error('Security context could not be loaded for the authenticated user.');
            }
        } catch (error) {
            await this.clearFailedLoginState();
            throw error;
        }

        this._loginChangedSubject.next(!!user && !user.expired);
        return user;

        // // SignalR Hub Connection (commented for future use)
        // const hubConnection = new HubConnectionBuilder().withUrl(environment.hubUrl + '/view').build();
        // hubConnection
        //     .start()
        //     .then(() => {
        //         hubConnection.send('NotifyWatching', 'Hello from Angular app!');
        //         console.log('Connection started');
        //     })
        //     .catch((error) => console.log('signalR: connection failed', error));
        //
        // hubConnection.on('viewCountUpdate', (value: number) => {
        //     console.log('*** SignalR : viewCountUpdate', value);
        // });
    }

    /** Initiate manual logout */
    public logout(): void {
        void this.performLogoutFlow('manual');
    }

    /**
     * Complete logout after redirect from IdentityServer.
     *
     * `logoff()` already clears local tokens before redirecting to the STS end
     * session endpoint, so on return we only clear the app-level session state
     * and navigate to the login page.
     */
    public async completeLogout(): Promise<void> {
        const reason = this.readPersistedLogoutReason() ?? this.pendingLogoutReason ?? 'manual';
        await this.clearSessionState();
        await this.navigateAfterLogout(reason);
    }

    /** Silently renew access token */
    public async renewToken(): Promise<User> {
        if (this.isSessionTerminationInProgress()) {
            throw new Error('login_required');
        }

        if (this.tokenRenewInFlight) {
            return this.tokenRenewInFlight;
        }

        if (this.automaticRenewalInFlight) {
            const automaticallyRenewedUser = await this.automaticRenewalInFlight;
            if (this.isUsableRenewedUser(automaticallyRenewedUser)) {
                return automaticallyRenewedUser;
            }

            // A transient automatic-renew error is not proof that the refresh
            // token is unusable. Confirm once through the app-level single-flight
            // flow before ending the session.
            if (this.isSessionTerminationInProgress()) {
                throw new Error('login_required');
            }

            if (this.tokenRenewInFlight) {
                return this.tokenRenewInFlight;
            }
        }

        const renewPromise = this.performTokenRenewal();
        this.tokenRenewInFlight = renewPromise;

        try {
            return await renewPromise;
        } finally {
            this.tokenRenewInFlight = null;
        }
    }

    /**
     * Recover a request rejected with 401.
     *
     * Automatic renewal can complete while an older request is in flight. Reuse
     * that newer session when available; otherwise enter the single deduplicated
     * manual refresh flow.
     */
    public async recoverFromUnauthorized(rejectedAccessToken: string): Promise<User> {
        if (this.isSessionTerminationInProgress()) {
            throw new Error('login_required');
        }

        const current = await this.loadCurrentUser();
        if (
            current &&
            !this.isTokenExpired(current) &&
            current.access_token &&
            current.access_token !== rejectedAccessToken
        ) {
            this.syncUser(current);
            return current;
        }

        return this.renewToken();
    }

    /** Refresh the session using the refresh-token grant and map the result. */
    private async performTokenRenewal(canRetryAfterAutomaticCollision = true): Promise<User> {
        try {
            // Avoid invoking the library's refresh flow when login never
            // completed or another flow has already cleared the local session.
            const refreshToken = await firstValueFrom(this.oidcSecurityService.getRefreshToken());
            if (!refreshToken) {
                throw new Error('login_required');
            }

            const response = await firstValueFrom(this.oidcSecurityService.forceRefreshSession());

            if (!response || !response.isAuthenticated || !response.accessToken) {
                // v15 returns an empty result when a periodic silent renew won
                // the race. Await that operation instead of treating the
                // collision as a failed session.
                if (this.automaticRenewalInFlight) {
                    const automaticallyRenewedUser = await this.automaticRenewalInFlight;
                    if (this.isUsableRenewedUser(automaticallyRenewedUser)) {
                        return automaticallyRenewedUser;
                    }

                    if (canRetryAfterAutomaticCollision && !this.isSessionTerminationInProgress()) {
                        return this.performTokenRenewal(false);
                    }
                }
                throw new Error(response?.errorMessage || 'login_required');
            }

            if (this.isSessionTerminationInProgress()) {
                throw new Error('login_required');
            }

            const user = this.buildUserFromLoginResponse(response);
            this.handleUserLoaded(user);
            return user;
        } catch (error) {
            // A token may disappear between the pre-check and refresh when a
            // concurrent logout or failed callback clears the OIDC storage.
            // Normalize that race to the expected unauthenticated outcome.
            if (this.isMissingRefreshTokenError(error)) {
                throw new Error('login_required');
            }

            throw error;
        }
    }

    /**
     * Initialize user state at app startup.
     *
     * `checkAuth()` both processes the redirect callback (when the app boots on
     * the signin-callback URL) and rehydrates an existing session from storage /
     * a silent renew, so it replaces the old `UserManager.getUser()` bootstrap.
     */
    public initializeUser(): Promise<void> {
        return firstValueFrom(this.oidcSecurityService.checkAuth())
            .then((response) => {
                this.oidcInitializationCompleted = true;
                if (response?.isAuthenticated && response.accessToken) {
                    this.handleUserLoaded(this.buildUserFromLoginResponse(response));
                } else {
                    this.handleUserLoaded(null);
                }
            })
            .catch((error) => {
                this.oidcInitializationCompleted = true;
                this.handleUserLoaded(null);
                this.loggingService.error('[AuthService] Error initializing user', error);
            });
    }

    // Public User Information Methods
    public getAccessToken(): string {
        return this.getUser()?.access_token ?? '';
    }

    public getClaims() {
        return this._user?.profile;
    }

    public getUser(): User | undefined {
        if (this._user && this.isTokenExpired(this._user)) {
            this._user.expired = true;
            return undefined;
        }

        return this._user;
    }

    public async getUserInfo(): Promise<User | null> {
        const user = await this.loadCurrentUser();
        this.loggingService.debug('[AuthService] Retrieved user info', user?.profile);
        return user && !this.isTokenExpired(user) ? user : null;
    }

    /** Load user permissions and authorization context from API */
    private async loadSecurityContext(options: SecurityContextLoadOptions, sequenceId: number): Promise<boolean> {
        const existingPlatformMode = this.authStore.getPlatformMode();

        try {
            const context = await firstValueFrom(
                this._httpClient.get<AuthContext>(`${environment.apiRoot}Auth/AuthContext`)
            );

            if (!this.isCurrentSecurityContextLoad(sequenceId)) {
                this.loggingService.debug('[AuthService] Ignoring stale AuthContext response.', { sequenceId });
                return !!this.authStore.getPlatformMode();
            }

            this.authContext = new AuthContext();
            // The API returns claims as a flat dict {type: value}; normalize to
            // UserClaim[] so AuthContext getters (.find) work correctly.
            const rawClaims = context.claims;
            const normalizedClaims = Array.isArray(rawClaims)
                ? rawClaims
                : Object.entries(rawClaims as Record<string, string>).map(([type, value]) => ({ type, value }));
            this.authContext.claims = normalizedClaims;
            this.authContext.userProfile = context.userProfile;

            const platformMode = this.resolvePlatformMode(normalizedClaims);
            if (platformMode) {
                this.authStore.setPlatformMode(platformMode);
                return true;
            }

            if (options.force) {
                this.authStore.setPlatformMode(null);
                this.loggingService.error('[AuthService] AuthContext returned missing or unsupported platform_mode during login.');
                return false;
            }

            if (existingPlatformMode) {
                this.loggingService.warn('[AuthService] AuthContext returned missing or unsupported platform_mode. Preserving existing stored mode.');
                return true;
            }

            this.authStore.setPlatformMode(null);
            this.loggingService.warn('[AuthService] AuthContext returned missing or unsupported platform_mode and no existing mode is available.');
            return false;
        } catch (error) {
            if (!this.isCurrentSecurityContextLoad(sequenceId)) {
                this.loggingService.debug('[AuthService] Ignoring stale AuthContext failure.', { sequenceId });
                return !!this.authStore.getPlatformMode();
            }

            this.loggingService.error('[AuthService] Failed to load security context', error);

            if (options.force) {
                this.authStore.setPlatformMode(null);
                return false;
            }

            if (existingPlatformMode) {
                this.loggingService.warn('[AuthService] AuthContext load failed. Preserving existing platformMode.');
                return true;
            }

            return false;
        }
    }

    private isCurrentSecurityContextLoad(sequenceId: number): boolean {
        return sequenceId === this.securityContextLoadSequence;
    }

    /** Extract and validate platform_mode before storing it for route guards. */
    private resolvePlatformMode(claims: unknown): PlatformMode | null {
        const platformMode = this.getClaimValue(claims, AUTH_CONTEXT_CLAIMS.PLATFORM_MODE);
        if (isPlatformMode(platformMode)) {
            return platformMode;
        }

        if (platformMode) {
            this.loggingService.warn('[AuthService] Unsupported platform_mode received from AuthContext.', {
                platformMode,
            });
        }

        return null;
    }

    private getClaimValue(claims: unknown, claimType: string): string | null {
        if (!claims) {
            return null;
        }

        if (Array.isArray(claims)) {
            const matchedClaim = claims.find((claim: any) => claim?.type === claimType);
            return typeof matchedClaim?.value === 'string' ? matchedClaim.value.trim() : null;
        }

        if (typeof claims === 'object') {
            const claimValue = (claims as Record<string, unknown>)[claimType];
            return typeof claimValue === 'string' ? claimValue.trim() : null;
        }

        return null;
    }

    private isUsableRenewedUser(user: User | null): user is User {
        return !!user && !this.isTokenExpired(user) && !!user.access_token;
    }

    /** Distinguish terminal OAuth/session errors from recoverable transport failures. */
    private isTerminalRenewalError(error: unknown): boolean {
        const text = this.collectRenewalErrorText(error).toLowerCase();
        const terminalCodes = [
            'invalid_grant',
            'login_required',
            'interaction_required',
            'consent_required',
            'account_selection_required',
            'access_denied',
            'invalid_client',
            'unauthorized_client',
            'no refresh token found',
        ];

        if (terminalCodes.some((code) => text.includes(code))) {
            return true;
        }

        return (
            text.includes('refresh token') &&
            (text.includes('expired') || text.includes('invalid') || text.includes('revoked') || text.includes('missing'))
        );
    }

    private collectRenewalErrorText(error: unknown, depth = 0): string {
        if (depth > 2 || error == null) {
            return '';
        }

        if (typeof error === 'string') {
            return error;
        }

        if (error instanceof Error) {
            const nestedError = (error as Error & { error?: unknown }).error;
            return `${error.message} ${this.collectRenewalErrorText(nestedError, depth + 1)}`;
        }

        if (typeof error === 'object') {
            const value = error as Record<string, unknown>;
            return [value['message'], value['error'], value['error_description']]
                .map((part) => this.collectRenewalErrorText(part, depth + 1))
                .join(' ');
        }

        return '';
    }

    // Private Initialization
    /**
     * React to the library's authentication state.
     *
     * `isAuthenticated$` emits after login, silent (refresh-token) renew and
     * logout, replacing the old `UserManager` `userLoaded`/`userSignedOut`
     * events so app stores and the idle timer stay in sync with the session.
     */
    private registerOidcEvents(): void {
        this.publicEventsService.registerForEvents().subscribe((event) => {
            switch (event.type) {
                case EventTypes.SilentRenewStarted:
                    this.beginAutomaticRenewalWait();
                    break;

                case EventTypes.SilentRenewFailed:
                    const terminalRenewalFailure = this.isTerminalRenewalError(event.value);
                    this.loggingService.warn(
                        terminalRenewalFailure
                            ? '[AuthService] Automatic token renewal failed with a terminal session error'
                            : '[AuthService] Automatic token renewal failed transiently; recovery remains available'
                    );
                    this.completeAutomaticRenewalWait(null);
                    if (this.oidcInitializationCompleted && terminalRenewalFailure) {
                        this.notifySessionExpired();
                    }
                    break;

                case EventTypes.NewAuthenticationResult:
                    this.handleAuthenticationResultEvent(event.value as {
                        isAuthenticated?: boolean;
                        isRenewProcess?: boolean;
                    });
                    break;
            }
        });

        this.oidcSecurityService.isAuthenticated$.subscribe(({ isAuthenticated }) => {
            // isAuthenticated$ emits its initial false value before checkAuth()
            // has processed a redirect callback. Acting on that value races the
            // callback and the app-settings initializer.
            if (!this.oidcInitializationCompleted) {
                return;
            }

            if (isAuthenticated) {
                void this.loadCurrentUser()
                    .then((user) => this.handleUserLoaded(user))
                    .catch((error) =>
                        this.loggingService.error('[AuthService] Failed to load user after auth change', error)
                    );
            } else {
                this.handleUserLoaded(null);
            }
        });
    }

    private beginAutomaticRenewalWait(): void {
        if (this.automaticRenewalInFlight || this.isSessionTerminationInProgress()) {
            return;
        }

        this.automaticRenewalInFlight = new Promise<User | null>((resolve) => {
            this.automaticRenewalResolver = resolve;
        });

        this.automaticRenewalTimeoutHandle = setTimeout(() => {
            this.loggingService.warn('[AuthService] Automatic token renewal timed out');
            this.completeAutomaticRenewalWait(null);
            this.notifySessionExpired();
        }, this.AUTOMATIC_RENEWAL_TIMEOUT_MS);
    }

    private handleAuthenticationResultEvent(result: {
        isAuthenticated?: boolean;
        isRenewProcess?: boolean;
    } | undefined): void {
        if (!result?.isRenewProcess) {
            return;
        }

        if (!result.isAuthenticated) {
            this.completeAutomaticRenewalWait(null);
            this.notifySessionExpired();
            return;
        }

        void this.loadCurrentUser()
            .then((user) => {
                if (user && !this.isTokenExpired(user) && !this.isSessionTerminationInProgress()) {
                    this.loggingService.info('[AuthService] Token renewed successfully.');
                    this.handleUserLoaded(user);
                    this.completeAutomaticRenewalWait(user);
                    return;
                }

                this.completeAutomaticRenewalWait(null);
                this.notifySessionExpired();
            })
            .catch((error) => {
                this.loggingService.error('[AuthService] Failed to load renewed session', error);
                this.completeAutomaticRenewalWait(null);
                this.notifySessionExpired();
            });
    }

    private completeAutomaticRenewalWait(user: User | null): void {
        if (this.automaticRenewalTimeoutHandle !== null) {
            clearTimeout(this.automaticRenewalTimeoutHandle);
            this.automaticRenewalTimeoutHandle = null;
        }

        const resolve = this.automaticRenewalResolver;
        this.automaticRenewalResolver = null;
        this.automaticRenewalInFlight = null;
        resolve?.(user);
    }

    /** Read the current session from the library and map it to the app user shape. */
    private async loadCurrentUser(): Promise<User | null> {
        const isAuthenticated = await firstValueFrom(this.oidcSecurityService.isAuthenticated());
        if (!isAuthenticated) {
            return null;
        }

        const [userData, accessToken, idToken] = await Promise.all([
            firstValueFrom(this.oidcSecurityService.getUserData()),
            firstValueFrom(this.oidcSecurityService.getAccessToken()),
            firstValueFrom(this.oidcSecurityService.getIdToken()),
        ]);

        if (!accessToken) {
            return null;
        }

        return this.buildUser(userData, accessToken, idToken);
    }

    /** Map a `LoginResponse` (from checkAuth/forceRefreshSession) to the app user shape. */
    private buildUserFromLoginResponse(response: LoginResponse): User {
        return this.buildUser(response.userData, response.accessToken, response.idToken);
    }

    /** Assemble the framework-agnostic user object consumed across the app. */
    private buildUser(userData: any, accessToken: string, idToken: string): User {
        const payload = this.decodeJwtPayload(accessToken);
        const expiresAt = typeof payload?.exp === 'number' ? payload.exp : undefined;
        const nowInSeconds = Math.floor(Date.now() / 1000);
        const userProfile = userData && typeof userData === 'object' ? userData : {};
        // UserInfo can be absent or incomplete during a refresh-token renewal.
        // The already-validated access token remains the authoritative fallback
        // for identity and tenant claims during that same authenticated session.
        const profile = {
            ...(payload ?? {}),
            ...userProfile,
            sub: this.getClaimValue(userProfile, 'sub') || this.getClaimValue(payload, 'sub') || undefined,
            tenant_id:
                this.getClaimValue(userProfile, 'tenant_id') ||
                this.getClaimValue(payload, 'tenant_id') ||
                undefined,
            database_name:
                this.getClaimValue(userProfile, 'database_name') ||
                this.getClaimValue(payload, 'database_name') ||
                undefined,
        };

        return {
            profile,
            access_token: accessToken ?? '',
            id_token: idToken || undefined,
            expires_at: expiresAt,
            expired: expiresAt !== undefined ? expiresAt <= nowInSeconds : !accessToken,
        };
    }

    /** Cache the freshest user without re-running the full authenticated-user flow. */
    private syncUser(user: User): void {
        this._user = user;
        this.userService.user = user;

        // `loadCurrentUser()` can observe a token that was renewed by the OIDC
        // library before its public event handler finishes. Keep the app stores
        // in sync as well, otherwise the Python interceptor could overwrite the
        // fresh request token with an older cached value.
        const mappedUser = this.extractUserClaims(user);
        this.updateUserStores(mappedUser, user);
    }

    /** Decode a JWT payload without validating the signature. */
    private decodeJwtPayload(token: string | undefined): { [key: string]: any } | null {
        if (!token) {
            return null;
        }

        try {
            const parts = token.split('.');
            if (parts.length < 2) {
                return null;
            }

            const payloadB64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const paddedPayload = payloadB64.padEnd(payloadB64.length + (4 - (payloadB64.length % 4)) % 4, '=');
            return JSON.parse(atob(paddedPayload));
        } catch (error) {
            this.loggingService.warn('[AuthService] Failed to decode token payload', error);
            return null;
        }
    }

    // Private Token Management

    private hasLiveAccessToken(): boolean {
        return !!this._user && !this.isTokenExpired(this._user);
    }

    /** True when a token has enough lifetime remaining for a protected request. */
    private isTokenReadyForRequest(user: User): boolean {
        if (this.isTokenExpired(user)) {
            return false;
        }

        if (user.expires_at === undefined) {
            return true;
        }

        const renewalSkewSeconds = this.TOKEN_RENEWAL_SKEW_SECONDS ?? 60;
        return user.expires_at > Math.floor(Date.now() / 1000) + renewalSkewSeconds;
    }

    /** Evaluate expiry against the current clock instead of a cached snapshot. */
    private isTokenExpired(user: User): boolean {
        if (!user.access_token || user.expired === true) {
            return true;
        }

        if (user.expires_at === undefined) {
            return false;
        }

        return user.expires_at <= Math.floor(Date.now() / 1000);
    }

    private extractAuthErrorMessage(error: unknown): string {
        if (error instanceof Error) {
            return error.message;
        }

        if (typeof error === 'string') {
            return error;
        }

        if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
            return error.message;
        }

        return '';
    }

    private isMissingRefreshTokenError(error: unknown): boolean {
        return this.extractAuthErrorMessage(error).toLowerCase().includes('no refresh token found');
    }

    private clearCachedAuthState(): void {
        this._user = undefined;
        this.userService.clearUser();
        this.authStore.clearUserContext();
        this.appSettingsStore.clearUserInfo();
        this.entityStore.clearSessionState();
        this.authContext = undefined;
        this.tokenRenewInFlight = null;
        this.completeAutomaticRenewalWait(null);
        this.securityContextLoadInFlight = null;
        this.securityContextLoadSequence++;
        this.tenantStateService.clearTenant();
        this._loginChangedSubject.next(false);
    }

    /**
     * Strict login failures must clear both app state and the OIDC user cache.
     * Otherwise a later app refresh can rehydrate an authenticated user without
     * a trusted platform_mode and produce inconsistent route access decisions.
     */
    private async clearFailedLoginState(): Promise<void> {
        try {
            this.oidcSecurityService.logoffLocal();
        } catch (error) {
            this.loggingService.warn('[AuthService] Failed to clear OIDC session after security context failure', error);
        } finally {
            this.clearCachedAuthState();
        }
    }

    // Private User State Management
    /** Handle user login or logout state changes */
    private handleUserLoaded(user: User | null): void {
        if (user && this.isSessionTerminationInProgress()) {
            this.loggingService.debug('[AuthService] Ignoring renewed user while session termination is in progress');
            return;
        }

        if (user && this.isTokenExpired(user)) {
            this.loggingService.warn('[AuthService] Received expired user, clearing session');
            this.handleUserLoaded(null);
            return;
        }

        if (user) {
            this.handleUserAuthenticated(user);
        } else {
            this.handleUserCleared();
        }
    }

    /** Update app state when user logs in */
    private handleUserAuthenticated(user: User): void {
        const hadExistingUser = !!this._user;
        const previousSubject = this._user?.profile?.sub || this.authStore.getUserId();
        const currentSubject = user.profile?.sub;
        const subjectChanged = previousSubject !== currentSubject;

        if (previousSubject && subjectChanged) {
            this.tenantStateService.clearTenant();
        }

        this._user = user;
        this.userService.user = user;

        if (!hadExistingUser) {
            this.lastActivityAt = this.getSharedRecentActivity() ?? Date.now();
        }

        this.sessionExpiredInProgress = false;
        this.logoutInProgress = false;
        this.registerActivityListeners();
        this.resetIdleTimer();

        if (!hadExistingUser) {
            this.broadcastActivity(true);
        }

        const mappedUser = this.extractUserClaims(user);
        this.updateUserStores(mappedUser, user);

        if (subjectChanged || !previousSubject) {
            this.entityStore.initializeFromAuth(mappedUser);
        }

        this._loginChangedSubject.next(true);
    }

    /** Clear app state when user logs out */
    private handleUserCleared(): void {
        this.unregisterActivityListeners();
        this.clearIdleTimer();
        this.lastActivityAt = Date.now();
        this._user = undefined;
        this.securityContextLoadInFlight = null;
        this.securityContextLoadSequence++;
        this.userService.clearUser();
        this.appSettingsStore.clearUserInfo();
        this.authStore.clearUserContext();
        this.entityStore.clearSessionState();
        this.authContext = undefined;
        this._loginChangedSubject.next(false);
    }

    /** Update app stores with user info */
    private updateUserStores(mappedUser: UserInfoDto, user: User): void {
        this.authStore.updateUserContext({
            userId: mappedUser.id,
            clientId: mappedUser.clientId,
            userData: user,
        });
        this.appSettingsStore.updateUserInfo(mappedUser);
    }

    /** Map OIDC user profile to app user model */
    private extractUserClaims(user: User): UserInfoDto {
        const profile = user.profile as any;

        const mappedUser: UserInfoDto = {
            id: profile.sub,
            clientId: profile.tenant_id || '',
            databaseName: profile.database_name || '',
            clientDisplayName: profile.client_display_name || '',
            clientSlug: profile.client_slug || '',
            clientLogo: profile.client_logo,
            clientColor: profile.client_color,
            subject: profile.sub || '',
            clientName: profile.client_name || '',
            docId: profile.doc_id || '',
            name: profile.given_name || profile.name || profile.preferred_username || '',
            email: profile.preferred_email || profile.email || '',
            role: profile.role || '',
        };

        this.loggingService.debug('[AuthService] Mapped user claims', mappedUser);

        return mappedUser;
    }

    // Private Idle Timeout
    /** Start listening for user activity */
    private registerActivityListeners(): void {
        if (typeof window === 'undefined' || this.idleEventsRegistered) {
            return;
        }

        this.IDLE_ACTIVITY_EVENTS.forEach((event) => {
            window.addEventListener(event, this.idleActivityListener, true);
        });
        this.idleEventsRegistered = true;
    }

    /** Stop listening for user activity */
    private unregisterActivityListeners(): void {
        if (typeof window === 'undefined' || !this.idleEventsRegistered) {
            return;
        }

        this.IDLE_ACTIVITY_EVENTS.forEach((event) => {
            window.removeEventListener(event, this.idleActivityListener, true);
        });
        this.idleEventsRegistered = false;
    }

    /** Track user activity and reset idle timer */
    public markActivity(): void {
        if (!this._user || this.isSessionTerminationInProgress()) {
            return;
        }

        this.lastActivityAt = Date.now();
        this.resetIdleTimer();
        this.broadcastActivity();
    }

    private handleUserActivity(): void {
        this.markActivity();
    }

    private clearIdleTimer(): void {
        if (typeof window === 'undefined' || this.idleTimeoutHandle === null) {
            return;
        }

        window.clearTimeout(this.idleTimeoutHandle);
        this.idleTimeoutHandle = null;
    }

    /** Reset idle timeout based on the explicit inactivity policy */
    private resetIdleTimer(): void {
        if (typeof window === 'undefined' || !this._user || this.isSessionTerminationInProgress()) {
            this.clearIdleTimer();
            return;
        }

        const idleDeadline = this.getIdleDeadline();
        const remainingMs = idleDeadline ? Math.max(idleDeadline - Date.now(), 0) : 0;

        this.clearIdleTimer();

        if (remainingMs <= 0) {
            this.handleIdleTimeout();
            return;
        }

        this.idleTimeoutHandle = window.setTimeout(() => this.handleIdleTimeout(), remainingMs);
    }

    /** Logout user due to inactivity */
    private handleIdleTimeout(): void {
        this.clearIdleTimer();

        if (!this._user || this.isSessionTerminationInProgress()) {
            return;
        }

        this.loggingService.info('[AuthService] Idle timeout reached, initiating automatic logout');
        void this.logoutDueToInactivity();
    }

    /**
     * Calculate when the session should be considered idle, based purely on the
     * explicit inactivity policy (decoupled from the access-token lifetime).
     */
    private getIdleDeadline(): number | null {
        const lastActivityAt = typeof this.lastActivityAt === 'number' ? this.lastActivityAt : Date.now();
        const idleTimeoutMs = this.IDLE_INACTIVITY_TIMEOUT_MS ?? 15 * 60 * 1000;
        return lastActivityAt + idleTimeoutMs;
    }

    // Private Cross-tab Session Coordination
    private registerCrossTabSessionEvents(): void {
        if (typeof window === 'undefined') {
            return;
        }

        window.addEventListener('storage', this.crossTabSessionListener);
    }

    private handleCrossTabSessionEvent(event: StorageEvent): void {
        if (event.key !== this.CROSS_TAB_SESSION_EVENT_KEY || !event.newValue) {
            return;
        }

        const sessionEvent = this.parseCrossTabSessionEvent(event.newValue);
        if (!sessionEvent) {
            return;
        }

        if (sessionEvent.type === 'activity') {
            if (this._user && !this.isSessionTerminationInProgress()) {
                this.lastActivityAt = Math.max(this.lastActivityAt, sessionEvent.at);
                this.resetIdleTimer();
            }
            return;
        }

        const reason = sessionEvent.reason ?? 'session-expired';
        if (this.logoutInProgress) {
            return;
        }

        this.loggingService.info('[AuthService] Session ended in another browser tab');
        this.logoutInProgress = true;
        this.sessionExpiredInProgress = reason === 'session-expired';
        void this.clearSessionState().then(() => this.navigateAfterLogout(reason));
    }

    private broadcastActivity(force = false): void {
        const now = Date.now();
        if (!force && now - this.lastActivityBroadcastAt < this.ACTIVITY_BROADCAST_THROTTLE_MS) {
            return;
        }

        this.lastActivityBroadcastAt = now;
        this.broadcastCrossTabSessionEvent({ type: 'activity', at: now });
    }

    private broadcastCrossTabSessionEvent(event: CrossTabSessionEvent): void {
        if (typeof localStorage === 'undefined') {
            return;
        }

        try {
            localStorage.setItem(this.CROSS_TAB_SESSION_EVENT_KEY, JSON.stringify(event));
        } catch (error) {
            this.loggingService.warn('[AuthService] Failed to coordinate session state across tabs', error);
        }
    }

    private getSharedRecentActivity(): number | null {
        if (typeof localStorage === 'undefined') {
            return null;
        }

        try {
            const rawEvent = localStorage.getItem(this.CROSS_TAB_SESSION_EVENT_KEY);
            const event = rawEvent ? this.parseCrossTabSessionEvent(rawEvent) : null;
            if (
                event?.type === 'activity' &&
                event.at <= Date.now() &&
                Date.now() - event.at < this.IDLE_INACTIVITY_TIMEOUT_MS
            ) {
                return event.at;
            }
        } catch (error) {
            this.loggingService.warn('[AuthService] Failed to read shared activity state', error);
        }

        return null;
    }

    private parseCrossTabSessionEvent(value: string): CrossTabSessionEvent | null {
        try {
            const event = JSON.parse(value) as Partial<CrossTabSessionEvent>;
            if ((event.type === 'activity' || event.type === 'logout') && typeof event.at === 'number') {
                return event as CrossTabSessionEvent;
            }
        } catch {
            // Ignore malformed, non-sensitive coordination data.
        }

        return null;
    }

    // Private Logout
    private logoutDueToInactivity(): Promise<void> {
        return this.performLogoutFlow('idle');
    }

    /**
     * Single central handler for session-expired signals. Debounces bursts of
     * concurrent signals and performs exactly one logout, preventing redirect
     * storms from parallel failing requests.
     */
    private registerSessionExpiredHandler(): void {
        this._sessionExpiredSubject
            .pipe(debounceTime(this.SESSION_EXPIRED_DEBOUNCE_MS))
            .subscribe(() => this.handleSessionExpired());
    }

    private handleSessionExpired(): void {
        this.loggingService.warn('[AuthService] Session expired signal received, performing single logout');
        void this.performLogoutFlow('session-expired');
    }

    /** Execute logout flow for manual or automatic logout */
    private async performLogoutFlow(reason: LogoutReason): Promise<void> {
        if (this.logoutInProgress) {
            return;
        }

        this.logoutInProgress = true;
        this.sessionExpiredInProgress = this.sessionExpiredInProgress || reason === 'session-expired';
        this.pendingLogoutReason = reason;
        this.persistLogoutReason(reason);
        this.broadcastCrossTabSessionEvent({ type: 'logout', at: Date.now(), reason });
        this.clearIdleTimer();
        this.unregisterActivityListeners();
        this.tenantStateService.clearTenant();

        let isAuthenticated = false;
        let hasRefreshToken = false;
        try {
            [isAuthenticated, hasRefreshToken] = await Promise.all([
                firstValueFrom(this.oidcSecurityService.isAuthenticated()),
                firstValueFrom(this.oidcSecurityService.getRefreshToken()).then((token) => !!token),
            ]);
        } catch (error) {
            this.loggingService.warn('[AuthService] Failed to read authentication state during logout', error);
        }

        // Use OIDC logout (redirect to the STS end-session endpoint) when a
        // session exists; revoke the refresh/access tokens first so a copied
        // refresh token cannot continue the session after sign-out.
        if (isAuthenticated || hasRefreshToken) {
            try {
                await firstValueFrom(this.oidcSecurityService.logoffAndRevokeTokens());
                return;
            } catch (error) {
                this.loggingService.error('[AuthService] OIDC token revocation/logout failed', error);

                try {
                    await firstValueFrom(this.oidcSecurityService.logoff());
                    return;
                } catch (logoutError) {
                    this.loggingService.error('[AuthService] OIDC logout redirect failed', logoutError);
                }
            }
        } else {
            this.loggingService.info('[AuthService] No active session, performing local cleanup only');
        }

        // Fallback: clean up locally and navigate
        await this.clearSessionState();
        await this.navigateAfterLogout(reason);
    }

    /** Clear local application and OIDC session state. */
    private async clearSessionState(): Promise<void> {
        this.clearIdleTimer();
        this.unregisterActivityListeners();

        try {
            this.oidcSecurityService.logoffLocal();
        } catch (error) {
            this.loggingService.warn('[AuthService] Failed to clear local OIDC session', error);
        }

        this.tokenRenewInFlight = null;
        this.completeAutomaticRenewalWait(null);
        this.securityContextLoadInFlight = null;
        this.securityContextLoadSequence++;
        this.handleUserLoaded(null);
        this.pendingLogoutReason = null;
        this.tenantStateService.clearTenant();
        localStorage.removeItem('access_token');
        // Clear cross-tab activity coordination so a re-login does not inherit
        // a stale timestamp that would fire the idle timer immediately.
        try { localStorage.removeItem(this.CROSS_TAB_SESSION_EVENT_KEY); } catch { /* best-effort */ }
        this.clearPersistedLogoutReason();
    }

    /** Navigate to login page after logout */
    private async navigateAfterLogout(reason: LogoutReason | null): Promise<void> {
        try {
            const queryParams =
                reason === 'idle'
                    ? { reason: 'idle-timeout' }
                    : reason === 'session-expired'
                      ? { reason: 'session-expired' }
                      : undefined;
            await this.router.navigate([this.POST_LOGOUT_ROUTE], { replaceUrl: true, queryParams });
            this.loggingService.info(`[AuthService] Logout complete (${reason}), navigated to login`);
        } catch (error) {
            this.loggingService.error('[AuthService] Navigation after logout failed', error);
        }
    }

    private persistLogoutReason(reason: LogoutReason): void {
        try {
            sessionStorage.setItem(this.LOGOUT_REASON_STORAGE_KEY, reason);
        } catch (error) {
            this.loggingService.warn('[AuthService] Failed to persist logout reason', error);
        }
    }

    private readPersistedLogoutReason(): LogoutReason | null {
        try {
            const reason = sessionStorage.getItem(this.LOGOUT_REASON_STORAGE_KEY);
            return reason === 'manual' || reason === 'idle' || reason === 'session-expired' ? reason : null;
        } catch {
            return null;
        }
    }

    private clearPersistedLogoutReason(): void {
        try {
            sessionStorage.removeItem(this.LOGOUT_REASON_STORAGE_KEY);
        } catch {
            // Best-effort cleanup of non-sensitive navigation state.
        }
    }
}
